import { ReactNode, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import styled from 'styled-components';
import { ui } from './tokens';
import CloseSvg from '../../assets/design/close.svg';
import { lockCanvasInput, unlockCanvasInput } from '../../libs/BitmapEditor/bitmapMaker3';

export type CardProps = {
	open: boolean;
	title: string;
	/** 1-based step number; with totalSteps renders the gold "01" and progress bar */
	step?: number;
	totalSteps?: number;
	description?: ReactNode;
	/** Shows the circular close button when provided */
	onClose?: () => void;
	children?: ReactNode;
	/** Small print under the content */
	note?: ReactNode;
	labelledBy?: string;
};

/**
 * Modal card used across the editor (style guide pages 1 and 3): #333 card,
 * Poppins Bold title, optional gold step number with progress bar and a
 * circular close button. Mirrors the app's StepCard so both feel like one flow.
 */
function Card({ open, title, step, totalSteps, description, onClose, children, note, labelledBy }: CardProps) {
	const progress = step && totalSteps ? Math.min(1, step / totalSteps) : 0;

	// Stop taps on the card from painting the canvas underneath
	useEffect(() => {
		if (!open) return;
		lockCanvasInput();
		return () => unlockCanvasInput();
	}, [open]);
	const titleId = labelledBy ?? `card-${title.replace(/\s+/g, '-').toLowerCase()}`;
	return (
		<Modal open={open} onClose={onClose} aria-labelledby={titleId} slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.6)' } } }}>
			<Panel>
				{onClose && (
					<CloseButton type="button" onClick={onClose} aria-label="Close">
						<img src={CloseSvg} alt="" width={18} height={18} />
					</CloseButton>
				)}
				<Title id={titleId} $withClose={!!onClose}>
					{title}
				</Title>
				{step && totalSteps ? (
					<>
						<StepNumber>{String(step).padStart(2, '0')}</StepNumber>
						<Progress>
							<ProgressFill style={{ width: `${progress * 100}%` }} />
						</Progress>
					</>
				) : null}
				{description && <Description>{description}</Description>}
				{children}
				{note && <Note>{note}</Note>}
			</Panel>
		</Modal>
	);
}

export default Card;

const Panel = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: calc(100% - 30px);
	max-width: 500px;
	max-height: 92vh;
	overflow-y: auto;
	background-color: ${ui.card};
	color: ${ui.text};
	border-radius: 12px;
	padding: 28px 25px;
	font-family: ${ui.font};
	outline: none;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
	position: absolute;
	top: 18px;
	right: 18px;
	width: 34px;
	height: 34px;
	border-radius: 50%;
	border: 2px solid ${ui.text};
	background: transparent;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
`;

const Title = styled.h2<{ $withClose: boolean }>`
	margin: 0;
	padding-right: ${props => (props.$withClose ? '40px' : '0')};
	font-size: 22px;
	font-weight: 700;
	line-height: 1.25;
`;

const StepNumber = styled.div`
	margin-top: 10px;
	font-size: 32px;
	font-weight: 600;
	line-height: 1.2;
	color: ${ui.gold};
`;

const Progress = styled.div`
	margin-top: 10px;
	height: 10px;
	border-radius: 5px;
	background-color: ${ui.text};
	overflow: hidden;
`;

const ProgressFill = styled.div`
	height: 100%;
	border-radius: 5px;
	background-color: ${ui.gold};
	transition: width 0.25s ease;
`;

const Description = styled.div`
	margin-top: 16px;
	font-size: 14px;
	line-height: 1.45;
`;

const Note = styled.div`
	margin-top: 18px;
	font-size: 11px;
	line-height: 1.45;
	color: ${ui.text};
`;
